import { useState, useLayoutEffect } from 'react';
import {
  AlertMessage,
  Theme,
  IconStyle,
  useTranslation,
  PromotionalMessageType,
  IconAlignment,
} from '@nextgen-web-framework/all';

import { PromotionDetailsList } from './PromotionDetailsList/PromotionDetailsList';
import type { PromotionDetailsProps } from './types/propsTypes';
import { PromotionDetailsType, PromotionsEnum } from './types/enums';
import PromotionDetailsTabVariant from './PromotionDetailsTabVariant/PromotionDetailsTabVariant';
import styles from './PromotionDetails.module.scss';

const { SUCCESS, ACTION } = Theme;
const { APPLIED, POTENTIAL } = PromotionalMessageType;

export const PromotionDetails = ({
  firedPromotions,
  potentialPromotions,
  orderPageVariant,
  tabVariant,
  type,
  showDrawerOnly,
  onDrawerClose,
}: PromotionDetailsProps) => {
  const t = useTranslation();
  const isCouponVariant = type === PromotionDetailsType.COUPON;
  const partialPromotionAppliedText = t('promotion.promotionDetails.partialPromotionApplied');
  const appliedSinglePromotion = t('promotion.promotionDetails.appliedSinglePromotion');
  const multiplePotentialPromotionsAvailable = t('promotion.promotionDetails.multiplePotentialPromotionsAvailable');
  const multiplePromotionsApplied = t('promotion.promotionDetails.multiplePromotionsApplied');
  const orderVariantText = t(
    isCouponVariant
      ? 'promotion.promotionDetails.couponsAppliedInOrder'
      : 'promotion.promotionDetails.promotionsAppliedInOrder',
  );

  const [showPromotionDrawer, setShowPromotionDrawer] = useState(showDrawerOnly ?? false);
  const [promotionText, setPromotionText] = useState('');
  const [promotionClass, setPromotionClass] = useState(APPLIED);

  useLayoutEffect(() => {
    switch (checkPromitionsAppliedToEntry()) {
      case PromotionsEnum.Applied:
        setAppliedPromotionsText();
        break;

      case PromotionsEnum.Potential:
        setPotentialPromotionsText();
        break;

      default:
        setPotentialAndAppliedPromotionsText();
        break;
    }
  }, [firedPromotions, potentialPromotions, orderPageVariant, type]);

  const firedPromotionsLength = firedPromotions?.length;
  const potentialPromotionsLength = potentialPromotions?.length;

  const checkPromitionsAppliedToEntry = () => {
    if (firedPromotionsLength && potentialPromotionsLength) {
      return PromotionsEnum.Both;
    }

    if (firedPromotionsLength) {
      return PromotionsEnum.Applied;
    }
    return PromotionsEnum.Potential;
  };

  const setPotentialAndAppliedPromotionsText = () => {
    if (firedPromotionsLength && potentialPromotionsLength) {
      const count = firedPromotionsLength + potentialPromotionsLength;
      setPromotionText(
        `${partialPromotionAppliedText
          .replace('{{text}}', String(firedPromotionsLength))
          .replace('{{text1}}', String(count))}`,
      );
    }
    setPromotionClass(APPLIED);
  };

  const setAppliedPromotionsText = () => {
    if (firedPromotionsLength) {
      if (orderPageVariant) {
        setPromotionText(orderVariantText.replace('{{text}}', String(firedPromotionsLength)));
      } else if (firedPromotionsLength === 1) {
        setPromotionText(`${appliedSinglePromotion.replace('{{text}}', firedPromotions[0].promotion?.name ?? '')}`);
      } else if (firedPromotionsLength > 1) {
        setPromotionText(`${multiplePromotionsApplied.replace('{{text}}', String(firedPromotionsLength))}`);
      }
      setPromotionClass(APPLIED);
    }
  };

  const setPotentialPromotionsText = () => {
    if (potentialPromotionsLength) {
      if (potentialPromotionsLength === 1) {
        setPromotionText(potentialPromotions[0].promotion?.name ?? '');
      }

      if (potentialPromotionsLength > 1) {
        setPromotionText(
          `${multiplePotentialPromotionsAvailable.replace('{{text}}', String(potentialPromotionsLength))}`,
        );
      }
    }
    setPromotionClass(POTENTIAL);
  };

  const handleDrawerClose = () => {
    setShowPromotionDrawer(false);
    onDrawerClose?.();
  };

  return (
    <div data-testid="promotion-details-component">
      {!showDrawerOnly && (
        <AlertMessage
          className={styles['promotion-alert-message']}
          theme={promotionClass === APPLIED ? SUCCESS : ACTION}
          icon="tags"
          iconStyle={IconStyle.SOLID}
          iconAlignment={IconAlignment.CENTER}
          onClick={() => setShowPromotionDrawer(true)}
        >
          <span>{promotionText}</span>
        </AlertMessage>
      )}
      {showPromotionDrawer &&
        (tabVariant ? (
          <PromotionDetailsTabVariant
            firedPromotions={firedPromotions}
            potentialPromotions={potentialPromotions}
            closeDrawer={handleDrawerClose}
            couponVariant={isCouponVariant}
            orderPageVariant={orderPageVariant}
          />
        ) : (
          <PromotionDetailsList
            firedPromotions={firedPromotions}
            potentialPromotions={potentialPromotions}
            closeDrawer={handleDrawerClose}
            couponVariant={isCouponVariant}
            orderPageVariant={orderPageVariant}
          />
        ))}
    </div>
  );
};

export default PromotionDetails;
export type { PromotionDetailsProps };
export { PromotionDetailsType };
